import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ButtonPrimary from "./components/ButtonPrimary";
import logo from "./resources/cadmus_dark.svg";
import apiClient from "./utils/apiClient";
import { useRegister } from "./RegisterContext";

// Pricing config
const PRICE_PER_ITEM = 1.25; // €1.25 per item per month
const MIN_ITEMS = 0;
const MAX_ITEMS = 1000;
const DEFAULT_ITEMS = 140;
const TYPICAL_ORDERS = 300;

function PricingScreen() {
  const navigate = useNavigate();
  const { registerData } = useRegister();
  const [items, setItems] = useState(DEFAULT_ITEMS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const estimatedCost = (items * PRICE_PER_ITEM).toFixed(2).replace(".", ",");
  const sliderPercent = ((items - MIN_ITEMS) / (MAX_ITEMS - MIN_ITEMS)) * 100;

  const handleContinueToPayment = async () => {
    setLoading(true);
    setError("");

    try {
      // Login met de zojuist aangemaakte credentials
      const response = await apiClient.post("/login", {
        email: registerData.email,
        password: registerData.password,
      });

      if (response?.data?.token) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("organization_id", response.data.organization_id);
        localStorage.setItem("company", response.data.company);
        localStorage.setItem("role", response.data.role);
        localStorage.setItem("org_type", response.data.org_type || "");
        localStorage.setItem("username", response.data.username);
        localStorage.setItem("avatar", response.data.avatar);
        localStorage.setItem("multicompany", response.data.multicompany === 1 ? "true" : "false");
        localStorage.setItem("email", response.data.email);

        navigate("/");
      } else {
        setError("Login failed. Please sign in manually.");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (err) {
      console.error("Auto-login error:", err);
      // Fallback: stuur naar login scherm
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <img
          src={logo}
          alt="Cadmus Logo"
          className="logo"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        />

        <span className="login-header">Transparent Pricing</span>
        <span className="login-sub-header">
          At Cadmus, you only pay for what you use.
          <br />
          No hidden costs. No monthly fees.
        </span>

        <div style={{ width: "100%", marginTop: "8px" }}>
          {/* Estimated items header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <span
              style={{
                fontFamily: "DM Sans",
                fontSize: "14px",
                fontWeight: "600",
                color: "#111",
              }}
            >
              Estimated items
            </span>
            <span
              style={{
                fontFamily: "DM Sans",
                fontSize: "14px",
                fontWeight: "500",
                color: "#111",
              }}
            >
              {items}
            </span>
          </div>

          {/* Slider */}
          <div style={{ position: "relative", marginBottom: "12px" }}>
            <input
              type="range"
              min={MIN_ITEMS}
              max={MAX_ITEMS}
              value={items}
              onChange={(e) => setItems(Number(e.target.value))}
              style={{
                width: "100%",
                height: "4px",
                appearance: "none",
                WebkitAppearance: "none",
                borderRadius: "2px",
                outline: "none",
                cursor: "pointer",
                background: `linear-gradient(to right, #6C44D4 0%, #6C44D4 ${sliderPercent}%, #E0E0E0 ${sliderPercent}%, #E0E0E0 100%)`,
              }}
            />
            <style>{`
              input[type='range']::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background: #6C44D4;
                border: 2.5px solid white;
                box-shadow: 0 0 0 1.5px #6C44D4;
                cursor: pointer;
              }
              input[type='range']::-moz-range-thumb {
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background: #6C44D4;
                border: 2.5px solid white;
                box-shadow: 0 0 0 1.5px #6C44D4;
                cursor: pointer;
              }
            `}</style>
          </div>

          {/* Typical lab note */}
          <div
            style={{
              background: "#F9F9F9",
              borderRadius: "6px",
              padding: "10px 14px",
              marginBottom: "20px",
            }}
          >
            <span
              style={{
                fontFamily: "DM Sans",
                fontSize: "13px",
                color: "#666",
              }}
            >
              A typical lab receives ~{TYPICAL_ORDERS} orders per month.
            </span>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px solid #EFEFEF", marginBottom: "14px" }} />

          {/* Estimated monthly cost */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "14px",
            }}
          >
            <span
              style={{
                fontFamily: "DM Sans",
                fontSize: "14px",
                color: "#666",
              }}
            >
              Estimated monthly cost
            </span>
            <span
              style={{
                fontFamily: "DM Sans",
                fontSize: "14px",
                color: "#666",
              }}
            >
              €{estimatedCost}
            </span>
          </div>

          {/* Divider */}
          <div style={{ borderTop: "1px solid #EFEFEF", marginBottom: "14px" }} />

          {/* Due today */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "28px",
            }}
          >
            <span
              style={{
                fontFamily: "DM Sans",
                fontSize: "14px",
                color: "#111",
              }}
            >
              Due today
            </span>
            <span
              style={{
                fontFamily: "DM Sans",
                fontSize: "22px",
                fontWeight: "700",
                color: "#111",
              }}
            >
              €0,00
            </span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <ButtonPrimary onClick={handleContinueToPayment} disabled={loading}>
          {loading ? "Signing in..." : "Continue to Payment"}
        </ButtonPrimary>
      </div>
    </div>
  );
}

export default PricingScreen;